/**
 * Map of HTML tag names to their corresponding element constructor classes
 * if more are needed, add them here and it will be available in the {@link is} object
 */
export const ELEMENT_CLASS_MAP = {
  Input: HTMLInputElement,
  TextArea: HTMLTextAreaElement,
  Element: Element,
  HTMLElement: HTMLElement,
  Node: Node,
} as const;

/**
 * Type representing all possible element class constructors
 */
export type ElementConstructor =
  (typeof ELEMENT_CLASS_MAP)[keyof typeof ELEMENT_CLASS_MAP];

/**
 * Type representing all possible element constructors
 */
export type TagName = keyof typeof ELEMENT_CLASS_MAP;

type DOMElementGuardObject = {
  [K in TagName]: (
    value: unknown,
  ) => value is InstanceType<(typeof ELEMENT_CLASS_MAP)[K]>;
};

/**
 * Type guards for DOM elements.
 * If you're unsure what element to check for, just use the base class is.Element
 *
 * @example
 * ```ts
 * const element = document.createElement('div');
 *
 * if (is.Element(element)) // element is instance of Element
 * if (is.HTMLElement(element)) // element is instance of HTMLElement
 * ```
 */
export const is = Object.fromEntries(
  Object.entries(ELEMENT_CLASS_MAP).map(([key, ElementClass]) => [
    key,
    (value: unknown) => value instanceof ElementClass,
  ]),
) as DOMElementGuardObject;
